/**
 * The inline reviews block on the product page — the shorter half of the
 * same surface the full list shows, and the place the stars-summary row
 * scrolls to.
 *
 * It composes the SAME `ReviewCard` the full list does, so the reference's
 * item order (item → rating → name → description → reviewer + time →
 * helpful) is stated once. Before this pass the two renderers each held
 * their own copy of the row and had already drifted: the inline one showed
 * the helpful count where the modal showed the word.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listProductReviews, toggleReviewHelpful } from '../../api/storeApi';
import ReviewLightbox from './ReviewLightbox';
import ReviewCard, { StarsRow } from './ReviewCard';
import styles from './reviewsSection.module.css';

const PREVIEW_LIMIT = 3;

const ReviewsSection = React.forwardRef(({
  productId, summary, onViewAll, t, shopName, shopLogo, product, productHref,
}, ref) => {
  const { i18n } = useTranslation();
  const locale = i18n?.language;
  const [reviews, setReviews] = useState([]);
  const [busyReviewId, setBusyReviewId] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!productId) return undefined;
    let cancelled = false;
    listProductReviews(productId, { sort: 'recommended', limit: PREVIEW_LIMIT }).then((list) => {
      if (!cancelled) setReviews(Array.isArray(list) ? list : []);
    });
    return () => { cancelled = true; };
  }, [productId]);

  const handleHelpful = async (id) => {
    if (busyReviewId) return;
    setBusyReviewId(id);
    try {
      const r = await toggleReviewHelpful(id);
      setReviews((prev) => prev.map((rv) => (rv.id === id ? { ...rv, helpfulCount: r.count, helpfulByMe: r.helpful } : rv)));
    } catch (_) { /* a guest vote 401s; the row stays as it was */ }
    finally { setBusyReviewId(null); }
  };

  const count = summary?.count ?? 0;

  return (
    <section ref={ref} className={styles.section} data-testid="product-detail-reviews-section">
      <div className={styles.header}>
        <div className={styles.headerMain}>
          <h2 className={styles.title}>{t('reviews.title', { defaultValue: 'Reviews' })}</h2>
          {count > 0 && (
            <div className={styles.avgRow}>
              <span className={styles.avgValue}>{Number(summary.average).toFixed(1)}</span>
              <StarsRow value={summary.average} size={20} t={t} testId="product-detail-reviews-average-stars" />
              <span className={styles.avgCount}>
                ({t('reviews.count', { defaultValue: '{{n}} reviews', n: count, count })})
              </span>
            </div>
          )}
        </div>
        {count > 0 && (
          <button
            type="button"
            className={styles.viewAllBtn}
            onClick={onViewAll}
            data-testid="product-detail-reviews-view-all"
          >
            {t('reviews.viewAll', { defaultValue: 'View all' })}
          </button>
        )}
      </div>

      {count === 0 ? (
        <p className={styles.empty}>{t('reviews.empty', { defaultValue: 'No reviews yet. Be the first!' })}</p>
      ) : (
        <div className={styles.list}>
          {reviews.map((r) => (
            <ReviewCard
              key={r.id}
              review={r}
              product={product}
              productHref={productHref}
              onHelpful={handleHelpful}
              helpfulBusy={busyReviewId === r.id}
              onPhoto={(photos, index) => setLightbox({ photos, index })}
              t={t}
              locale={locale}
              shopName={shopName}
              shopLogo={shopLogo}
              testIdPrefix="product-detail-review"
            />
          ))}

          {count > reviews.length && (
            <button
              type="button"
              className={styles.viewAllWide}
              onClick={onViewAll}
              data-testid="product-detail-reviews-view-all-wide"
            >
              {t('reviews.viewAll', { defaultValue: 'View all' })} ({count})
            </button>
          )}
        </div>
      )}
      {lightbox && (
        <ReviewLightbox
          photos={lightbox.photos}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onIndex={(i) => setLightbox((lb) => lb && ({ ...lb, index: i }))}
        />
      )}
    </section>
  );
});

ReviewsSection.displayName = 'ReviewsSection';

export default ReviewsSection;
